/**
 * Form Engine v2 — shared types used by backend (Zod validation) and frontend (renderer).
 *
 * The `schema` column on form_templates stores either:
 *   - FormSchemaV2 (version: "2") — new section-based format
 *   - { fields: FormField[] }     — legacy flat format (read-only, renderer shows upgrade notice)
 *
 * Use `isFormSchemaV2(schema)` to discriminate at runtime.
 */

// ── Field types ────────────────────────────────────────────────────────────────

export type FieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "datetime"
  | "time"
  | "phone"
  | "email"
  | "select"
  | "multiselect"
  | "radio"
  | "checkbox"
  | "switch"
  | "file"
  | "signature"
  | "resident_lookup"
  | "resident_dob"
  | "staff_lookup"
  | "facility_lookup"
  | "medication_lookup"
  | "resident_allergy_lookup"
  | "resident_emergency_contact_lookup"
  | "resident_insurance_lookup"
  | "resident_pharmacy_lookup"
  | "resident_care_plan_lookup"
  | "external_provider_lookup"
  | "calculated_field"
  | "body_map";

/**
 * US-133: field types backed by a real entity record — the field must resolve
 * to an actual {id, label} record via autocomplete, never static `options` or
 * free text. `resident_dob` is included: it is auto-filled from the resident's
 * profile and must never carry static options either, even though it isn't an
 * entity-picker UI. Single source of truth for both the AI-schema constraint
 * (backend/src/lib/validation/forms.ts) and the frontend renderer's dispatch
 * (FieldRenderer.tsx) — keep both consumers importing this instead of
 * re-listing the field types by hand.
 */
export const LOOKUP_FIELD_TYPES = [
  "resident_lookup",
  "resident_dob",
  "staff_lookup",
  "facility_lookup",
  "medication_lookup",
  "resident_allergy_lookup",
  "resident_emergency_contact_lookup",
  "resident_insurance_lookup",
  "resident_pharmacy_lookup",
  "resident_care_plan_lookup",
  "external_provider_lookup",
] as const satisfies readonly FieldType[];

export type LookupFieldType = (typeof LOOKUP_FIELD_TYPES)[number];

export type EntityPickerFieldType = Exclude<LookupFieldType, "resident_dob">;

/** Entity-picker field types that render EntityLookupField (excludes resident_dob, which has its own dedicated date UI). */
export const ENTITY_PICKER_FIELD_TYPES = LOOKUP_FIELD_TYPES.filter(
  (t): t is EntityPickerFieldType => t !== "resident_dob",
);

/**
 * Code-review fix (Altitude angle): {entityLabel, multiple} used to be a
 * hand-maintained `Partial<Record<...>>` inside frontend/FieldRenderer.tsx
 * with a silent runtime fallback if a type was missing an entry — a new
 * entity-picker type could be added to LOOKUP_FIELD_TYPES (compiler-enforced)
 * without anyone adding its metadata here (NOT compiler-enforced), and it
 * would silently render as single-select with a raw field-label instead of
 * erroring. A non-`Partial`, `satisfies`-checked Record here means a missing
 * entry is a compile error in THIS file, not a silent runtime fallback in a
 * consumer. Single source of truth for both FieldRenderer.tsx (frontend) and
 * anywhere else this metadata is needed.
 */
export const LOOKUP_FIELD_META: Record<
  EntityPickerFieldType,
  { entityLabel: string; multiple: boolean }
> = {
  resident_lookup: { entityLabel: "Resident", multiple: false },
  staff_lookup: { entityLabel: "Staff member", multiple: false },
  facility_lookup: { entityLabel: "Facility", multiple: false },
  medication_lookup: {
    entityLabel: "the resident's active medications",
    multiple: true,
  },
  resident_allergy_lookup: {
    entityLabel: "the resident's known allergies",
    multiple: true,
  },
  resident_emergency_contact_lookup: {
    entityLabel: "an emergency contact on file",
    multiple: false,
  },
  resident_insurance_lookup: {
    entityLabel: "insurance on file",
    multiple: false,
  },
  resident_pharmacy_lookup: {
    entityLabel: "a pharmacy on file",
    multiple: false,
  },
  resident_care_plan_lookup: {
    entityLabel: "the resident's active care plan",
    multiple: false,
  },
  external_provider_lookup: {
    entityLabel: "an external provider on file",
    multiple: false,
  },
};

/**
 * Code-review fix (Altitude angle): "does this field type allow static
 * `options`" used to be re-derived inline (`LOOKUP_FIELD_TYPES.includes(...)`)
 * at multiple call sites in backend/src/lib/ai-provider.ts, and described
 * separately again in the AI system prompt and the Zod schema's `.describe()`
 * text — the same rule, stated three ways with no single definition. This is
 * that one definition: every lookup type (including resident_dob, which is
 * auto-filled rather than a static list) disallows static options; every
 * other field type allows them.
 */
export function allowsStaticOptions(type: FieldType): boolean {
  return !(LOOKUP_FIELD_TYPES as readonly FieldType[]).includes(type);
}

/**
 * One real, backend-sourced choice for an entity-picker lookup field —
 * `value` is a unique per-option key (e.g. a medication's name+dosage string,
 * an emergency contact's id), `label` is the display text. Matches the
 * existing `medicationOptions` convention (verified this session:
 * `ResidentFormFillModal` already builds medication options as
 * `{ value: medicationNameAndDosage, label: sameString }` — the *stored*
 * field value has always been this label/name string, never a database FK —
 * so every lookup type shares that same stable, already-shipped contract
 * rather than introducing a new object shape that would need a
 * `form_submissions` data migration).
 */
export interface LookupOption {
  value: string;
  label: string;
}

// ── Semantic validation (US-133 follow-up — real-time hardening) ────────────────
//
// The AI-generation/review pipeline previously only validated STRUCTURE (does
// this parse as a FormSchemaV2 / does a field have a real FieldType). Nothing
// checked whether a field would actually WORK once rendered. Two concrete
// failures motivated this:
//   1. staff_lookup / facility_lookup used on a resident-audience form — both
//      are real field types, but ResidentFormFillModal never wires live
//      options for them (only StaffFormsSection / FacilityFormsSection /
//      SupervisedSessionPage do, each for their own audience). The field
//      renders as a permanent "resolved at fill time" placeholder, or — if
//      marked required — silently degrades to a plain unresolved text box.
//   2. A field capturing an SSN (or similar specially-protected identifier)
//      via plain text/number — bypassing the encryption + resident:ssn-read
//      gate the real residents.ssn_encrypted column gets.
//
// validateFormSchemaSemantics() is a pure, deterministic check for both —
// no LLM judgment call needed to catch a KNOWN class of mistake. It returns
// issues in the exact shape the AI review pipeline already uses
// (FormReviewIssue in backend/src/lib/ai-provider.ts), so the evaluate route
// can merge deterministic + LLM-found issues into one list with no frontend
// changes.

export const FORM_AUDIENCE_VALUES = [
  "resident",
  "staff",
  "facility",
  "external_provider",
] as const;
export type FormAudience = (typeof FORM_AUDIENCE_VALUES)[number];

/**
 * Which audiences have a REAL, live-data-backed option list wired for each
 * lookup type today — the single source of truth for both
 * validateFormSchemaSemantics (below) and the AI system prompt
 * (backend/src/app/api/ai/form-assistant/route.ts), which restricts the type
 * enum it offers the model to the current template's audience. Update this
 * the moment a new surface wires a lookup type's `lookupOptions` for real
 * (see ResidentFormFillModal.tsx / StaffFormsSection.tsx /
 * FacilityFormsSection.tsx / SupervisedSessionPage.tsx on the frontend) —
 * this list existing and being wrong is worse than it not existing.
 */
export const LOOKUP_FIELD_AUDIENCE_SUPPORT: Record<
  LookupFieldType,
  readonly FormAudience[]
> = {
  // Also wired on external_provider forms — SupervisedSessionPage.tsx
  // pre-populates both from the session's resident record for the visiting
  // provider's locked demographic fields.
  resident_lookup: ["resident", "external_provider"],
  resident_dob: ["resident", "external_provider"],
  staff_lookup: ["staff"],
  facility_lookup: ["facility", "external_provider"],
  medication_lookup: ["resident"],
  resident_allergy_lookup: ["resident"],
  resident_emergency_contact_lookup: ["resident"],
  resident_insurance_lookup: ["resident"],
  resident_pharmacy_lookup: ["resident"],
  resident_care_plan_lookup: ["resident"],
  external_provider_lookup: ["resident", "external_provider"],
};

/**
 * Field labels that suggest a specially-protected identifier is being
 * captured via a plain, unencrypted, ungated value — never via one of the
 * real *_lookup types (which resolve against an already-secured record), and
 * never behind a permission gate the way e.g. residents.ssn_encrypted is
 * (readable only through resident:ssn-read). A HIPAA-regulated form
 * shouldn't re-capture these at all.
 */
const SENSITIVE_LABEL_PATTERNS: ReadonlyArray<{
  pattern: RegExp;
  name: string;
}> = [
  { pattern: /\bssn\b|social\s*security/i, name: "Social Security Number" },
  { pattern: /driver.?s?\s*licen[cs]e/i, name: "driver's license number" },
  // Bare "Passport" (no trailing "number"/"no"/"#") is a common shorthand for
  // the passport number field itself — the suffix used to be mandatory, which
  // silently let the most common labeling of this field straight through.
  { pattern: /\bpassport\b/i, name: "passport number" },
  { pattern: /(?:credit|debit)\s*card|\bcvv\b/i, name: "credit card number" },
  // NOTE: intentionally does NOT match a bare "Account Number" — this app's
  // domain has legitimate non-financial uses of that phrase (e.g. a patient/
  // insurance billing account number), so only the unambiguous bank-specific
  // phrasing is flagged. A bare "Account Number" field is a known, accepted
  // gap here rather than an oversight.
  { pattern: /bank\s*account|routing\s*number/i, name: "bank account number" },
];

/** Types that store a raw, freely-typed value — the only ones a sensitive
 *  identifier could actually leak through in plain text. */
const FREE_TEXT_VALUE_TYPES = [
  "text",
  "number",
  "textarea",
] as const satisfies readonly FieldType[];

/**
 * Field types where `options` isn't merely supported but REQUIRED. The
 * renderer treats options as suggestions (fillers can also type free text),
 * but an optionless choice field is still an authoring mistake: it renders
 * with no suggestions at all, i.e. a plain text box pretending to be a
 * picker — the author should either supply real options or use text.
 * `checkbox` is deliberately excluded: with no options it's a legitimate
 * standalone boolean (see the checkbox multi-select fix in
 * FieldRenderer.tsx / formValueFormatter.ts), not a broken picker.
 */
export const CHOICE_FIELD_TYPES_REQUIRING_OPTIONS = [
  "select",
  "multiselect",
  "radio",
] as const satisfies readonly FieldType[];

/** Same shape as FormReviewIssue (backend/src/lib/ai-provider.ts) so the
 *  evaluate route can merge deterministic + LLM-found issues into one list. */
export interface FormSemanticIssue {
  fieldId: string;
  sectionId?: string;
  severity: "error" | "warning" | "info";
  message: string;
  suggestedType?: string;
}

/**
 * Deterministic (non-AI) validation pass over a generated/edited
 * FormSchemaV2 — catches mistakes an LLM review can miss, invent
 * inconsistently, or simply forget to re-check on every pass:
 *   1. A lookup field type with no live data source for this audience.
 *   2. A specially-protected identifier captured via plain text/number.
 *   3. A select/multiselect/radio field with no options defined — the same
 *      thing FormBuilderPage.tsx's publish-time gate catches, surfaced here
 *      immediately at generation/review time instead of only at publish.
 */
export function validateFormSchemaSemantics(
  schema: FormSchemaV2,
  audience: FormAudience,
): FormSemanticIssue[] {
  const issues: FormSemanticIssue[] = [];
  for (const section of schema.sections) {
    for (const element of section.elements) {
      if (element.kind !== "field") continue;

      const lookupSupport =
        LOOKUP_FIELD_AUDIENCE_SUPPORT[element.type as LookupFieldType];
      if (lookupSupport && !lookupSupport.includes(audience)) {
        issues.push({
          fieldId: element.id,
          sectionId: section.id,
          severity: "error",
          message:
            `"${element.label}" uses ${element.type}, which has no live data ` +
            `source on a "${audience}" form — it will never resolve to a real ` +
            `picker here (only ${lookupSupport.join(" / ")} form${
              lookupSupport.length === 1 ? "" : "s"
            } wire it today).`,
        });
      }

      if ((FREE_TEXT_VALUE_TYPES as readonly string[]).includes(element.type)) {
        const match = SENSITIVE_LABEL_PATTERNS.find((p) =>
          p.pattern.test(element.label),
        );
        if (match) {
          issues.push({
            fieldId: element.id,
            sectionId: section.id,
            severity: "error",
            message:
              `"${element.label}" looks like it captures a ${match.name} via ` +
              `plain, unencrypted, ungated text — do not collect this in a form field.`,
          });
        }
      }

      if (
        (CHOICE_FIELD_TYPES_REQUIRING_OPTIONS as readonly string[]).includes(
          element.type,
        ) &&
        !element.options?.length
      ) {
        issues.push({
          fieldId: element.id,
          sectionId: section.id,
          severity: "error",
          message:
            `"${element.label}" is a ${element.type} field with no options ` +
            `defined — it would render with nothing to suggest (a text box ` +
            `pretending to be a picker). Add real options, or use a text field.`,
        });
      }
    }
  }
  return issues;
}

// ── Conditions ────────────────────────────────────────────────────────────────

export type ConditionOperator =
  | "equals"
  | "notEquals"
  | "contains"
  | "greaterThan"
  | "lessThan"
  | "isEmpty"
  | "isNotEmpty";

export interface SimpleCondition {
  field: string;
  operator: ConditionOperator;
  value?: unknown;
}

export interface CompoundCondition {
  operator: "AND" | "OR";
  rules: Condition[];
}

export type Condition = SimpleCondition | CompoundCondition;

// ── Validation ────────────────────────────────────────────────────────────────

export interface ValidationRules {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  min?: number;
  max?: number;
  regex?: string;
  regexMessage?: string;
}

// ── Elements ──────────────────────────────────────────────────────────────────

export interface FieldOption {
  label: string;
  value: string;
}

export interface FieldLayout {
  columns?: 1 | 2 | 3 | 4;
  fullWidth?: boolean;
}

export interface FieldElement {
  kind: "field";
  id: string;
  type: FieldType;
  label: string;
  description?: string;
  placeholder?: string;
  options?: FieldOption[];
  validation?: ValidationRules;
  visibleIf?: Condition;
  requiredIf?: Condition;
  layout?: FieldLayout;
  // type-specific
  min?: number;
  max?: number;
  step?: number;
  multiple?: boolean;
  accept?: string;
  rows?: number;
  expression?: string; // for calculated_field
  /** When true, the field is pre-filled from context and cannot be edited by the form filler. */
  locked?: boolean;
}

export type StructuralType =
  "header" | "subheader" | "paragraph" | "divider" | "alert";

export interface StructuralElement {
  kind: "structural";
  id: string;
  type: StructuralType;
  content?: string;
}

export type FormElement = FieldElement | StructuralElement;

// ── Section ───────────────────────────────────────────────────────────────────

export interface FormSection {
  id: string;
  title: string;
  description?: string;
  collapsible?: boolean;
  columns?: 1 | 2 | 3 | 4;
  elements: FormElement[];
  visibleIf?: Condition;
}

// ── Design tokens ────────────────────────────────────────────────────────────
// All styling uses controlled tokens — never raw CSS values.
// The renderer maps these internally (see designTokens.ts on the frontend).

export type BorderColorToken = "neutral" | "muted" | "primary" | "danger";
export type FontSizeToken = "sm" | "md" | "lg" | "xl" | "2xl";
export type FontWeightToken = "regular" | "medium" | "semibold" | "bold";
export type LineHeightToken = "tight" | "normal" | "relaxed";
export type SpacingToken = "sm" | "md" | "lg" | "xl";
export type AlignmentToken = "left" | "center" | "right";
export type BackgroundToken = "white" | "light" | "muted" | "primary" | "brand";
export type LogoPositionToken = "top-left" | "center" | "right";

// ── Settings blocks ───────────────────────────────────────────────────────────

export interface PageSettings {
  borderEnabled?: boolean;
  borderColor?: BorderColorToken;
  /** 1px / 2px / 3px */
  borderWidth?: "sm" | "md" | "lg";
  padding?: SpacingToken;
  background?: "white" | "light" | "muted";
}

/** Title section rendered before form sections. Supports {{variable}} interpolation. */
export interface TitleSectionSettings {
  text?: string;
  subtitle?: string;
  alignment?: AlignmentToken;
  fontSize?: FontSizeToken;
  fontWeight?: FontWeightToken;
  lineHeight?: LineHeightToken;
  underline?: boolean;
  borderEnabled?: boolean;
  borderColor?: BorderColorToken;
  padding?: SpacingToken;
  background?: BackgroundToken;
  margin?: SpacingToken;
}

export interface BrandingSettings {
  /** Data URL or template variable like {{organization_logo}} */
  logo?: string;
  fallbackLogo?: "livingsyncs" | "none";
  position?: LogoPositionToken;
  /** Token-mapped logo width (px) */
  logoWidth?: SpacingToken;
  /** Token-mapped logo height (px) */
  logoHeight?: SpacingToken;
  showPoweredBy?: boolean;
  poweredByText?: string;
}

export interface FormSettings {
  page?: PageSettings;
  titleSection?: TitleSectionSettings;
  branding?: BrandingSettings;
}

// ── Schema v2 ─────────────────────────────────────────────────────────────────

export interface FormSchemaV2 {
  version: "2";
  sections: FormSection[];
  /** Optional display settings — all values are design tokens, not raw CSS. */
  settings?: FormSettings;
}

// ── Type guard ────────────────────────────────────────────────────────────────

export function isFormSchemaV2(schema: unknown): schema is FormSchemaV2 {
  return (
    typeof schema === "object" &&
    schema !== null &&
    (schema as { version?: unknown }).version === "2" &&
    Array.isArray((schema as { sections?: unknown }).sections)
  );
}

/**
 * True when a form schema has no actual content: `{}`, a legacy schema with no
 * fields, or a v2 schema whose sections hold no elements. Used to detect a
 * template that is "missing a body" — e.g. a state-required form that has
 * neither an external PDF link nor an authored in-app form.
 */
export function isFormSchemaEmpty(schema: unknown): boolean {
  if (isFormSchemaV2(schema)) {
    return schema.sections.every((s) => (s.elements?.length ?? 0) === 0);
  }
  // Legacy flat format: non-empty only if it carries at least one field.
  if (
    typeof schema === "object" &&
    schema !== null &&
    Array.isArray((schema as { fields?: unknown }).fields)
  ) {
    return (schema as { fields: unknown[] }).fields.length === 0;
  }
  // `{}` or anything unrecognised → treat as empty.
  return true;
}

/**
 * A form template is "content-incomplete" when it offers no way to be filled:
 * neither an external state-form link NOR an authored form body. Such a
 * template can't be completed by staff, so it should be flagged in the UI and
 * blocked from publishing.
 */
export function isFormTemplateContentMissing(input: {
  external_form_url?: string | null;
  schema: unknown;
}): boolean {
  const hasLink = !!input.external_form_url?.trim();
  return !hasLink && isFormSchemaEmpty(input.schema);
}

// ── Legacy types (kept for AI assistant + backend Zod compat) ─────────────────

/** Legacy flat field — used by AI assistant output and backend validation. */
export interface FormField {
  id: string;
  label: string;
  type:
    | "text"
    | "textarea"
    | "number"
    | "date"
    | "datetime-local"
    | "time"
    | "select"
    | "checkbox"
    | "radio"
    | "email"
    | "file"
    | "tel"
    | "phone"
    | "range";
  required?: boolean;
  options?: string[];
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  defaultValue?: string | number | boolean;
}

/** Legacy flat schema — still stored in DB for old templates. */
export interface LegacyFormSchema {
  fields: FormField[];
}

/** Adapter: convert AI assistant output (legacy FormField[]) to FieldElement[] */
export function adaptLegacyFields(fields: FormField[]): FieldElement[] {
  return fields.map((f): FieldElement => ({
    kind: "field",
    id: f.id,
    label: f.label,
    type: adaptLegacyFieldType(f.type),
    placeholder: f.placeholder,
    options: f.options?.map((o) => ({ label: o, value: o })),
    validation: f.required ? { required: true } : undefined,
    min: f.min,
    max: f.max,
    step: f.step,
  }));
}

function adaptLegacyFieldType(t: FormField["type"]): FieldType {
  const map: Record<FormField["type"], FieldType> = {
    text: "text",
    textarea: "textarea",
    number: "number",
    date: "date",
    "datetime-local": "datetime",
    time: "time",
    select: "select",
    checkbox: "checkbox",
    radio: "radio",
    email: "email",
    file: "file",
    tel: "phone",
    phone: "phone",
    range: "number", // pain scale (0-10) → number input
  };
  return map[t] ?? "text";
}
